import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE } from '@/utils/constants/apiEndpoints.ts';

import { getFilters } from '@/pages/physician/PatientsPage/util';
import {
  BatchDownloadCreateRequest,
  BatchDownloadCreateResponse,
  BatchDownloadGetResponse,
  ExportAllBatchRequest,
  ExportAllBatchResponse,
  UpdateBatchDownloadPdfInfoRequest
} from '@/types/batchDownload';

import { patientsApiSlice } from './patientsApiSlice';

const baseUrl = import.meta.env.VITE_APP_API_URL ?? 'http://localhost:8001';

export const batchDownloadApiSlice = createApi({
  reducerPath: 'batchDownloadApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.BatchDownload],
  endpoints: (builder) => ({
    createBatchDownload: builder.mutation<BatchDownloadCreateResponse, BatchDownloadCreateRequest>({
      query: (params) => ({
        url: API_ENDPOINTS.BATCH_DOWNLOAD.CREATE,
        method: 'POST',
        body: params
      })
    }),
    batchExportAll: builder.mutation<ExportAllBatchResponse, ExportAllBatchRequest>({
      query: (params) => ({
        url: API_ENDPOINTS.BATCH_DOWNLOAD.ALL,
        method: 'POST',
        body: params
      })
    }),
    getBatchDownload: builder.query<BatchDownloadGetResponse, number>({
      query: (id) => ({
        url: API_ENDPOINTS.BATCH_DOWNLOAD.GET(id),
        method: 'GET'
      })
    }),
    updateBatchDownloadPdfInfo: builder.mutation<any, UpdateBatchDownloadPdfInfoRequest>({
      query: (params) => ({
        url: API_ENDPOINTS.BATCH_DOWNLOAD.UPDATE_BATCH_PDF_DOWNLOAD_INFO,
        method: 'POST',
        body: params
      }),
      async onQueryStarted(_params, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Update Batch Download Pdf Info Mutation failed:', error);
        } finally {
          // NOTE: added the code in finally as the backend is returning the response in text format instead of json which is causing the rtk query to throw error everytime
          const state = getState() as RootState;
          const { filters: localFilters, pageLimit, skip } = state.patients;
          const { filters: globalFilters } = state.preferences;
          dispatch(
            patientsApiSlice.endpoints.getAllPatients.initiate(
              {
                skip,
                pageLimit: pageLimit ?? 10,
                forV2: true,
                ...getFilters(localFilters, globalFilters)
              },
              { forceRefetch: true }
            )
          );
        }
      }
    })
  })
});

export const {
  useCreateBatchDownloadMutation,
  useGetBatchDownloadQuery,
  useUpdateBatchDownloadPdfInfoMutation,
  useBatchExportAllMutation
} = batchDownloadApiSlice;

export const api = batchDownloadApiSlice;
